import React, {useCallback, useMemo} from 'react';
import {faFolder} from '@netx/core-icons';
import {Button} from '@mui/material';
import {
	type ExtendReactHookFormDialogProps,
	ReactHookFormDialog,
} from '@netx/core-components/FormDialog';
import {styled} from '@netx/core-components/Theme';
import {
	type ICategoryCollisionFormValues,
	useCategoryCollisionForm,
} from '../../hooks';
import {
	CategoryCollisionManagerActionLabel,
	CategoryCollisionManagerTitle,
} from '../../locales';
import {
	CategoryCollisionManager,
	type CategoryCollisionManagerProps,
} from './CategoryCollisionManager';

const CategoryCollisionDialogActions = styled('div')(({theme}) => ({
	display: 'flex',
	width: '100%',
	gap: theme.spacing(1),
}));

const CategoryCollisionDialogActionsLeft = styled('div')(({theme}) => ({
	display: 'flex',
	flex: '1 1 auto',
}));

const CategoryCollisionDialogActionsRight = styled('div')(({theme}) => ({
	display: 'flex',
	flex: '0 0 auto',
	gap: theme.spacing(1),
}));

export interface CategoryCollisionManagerDialogProps extends ExtendReactHookFormDialogProps<CategoryCollisionManagerProps> {
	onCancel?: () => void;
}

export const CategoryCollisionManagerDialog = ({
	form,
	open,
	onCancel,
	onClose,
	onSubmit,
	FormProps,
	DialogProps,
	...props
}: CategoryCollisionManagerDialogProps) => {
	const setSkipped = useCallback((skip: boolean) => {
		form.setValue('skipped', skip, {
			shouldDirty: true,
			shouldValidate: true,
			shouldTouch: true,
		});
	}, []);

	return (
		<ReactHookFormDialog
			open={open}
			requiresChanges={false}
			title={CategoryCollisionManagerTitle.TITLE}
			confirmLabel={CategoryCollisionManagerActionLabel.SUBMIT}
			cancelLabel={CategoryCollisionManagerActionLabel.CANCEL}
			{...FormProps}
			form={form}
			onClose={onClose}
			onSubmit={onSubmit}
			DialogProps={{
				fullScreenWhenMobile: true,
				fullWidth: true,
				icon: faFolder,
				maxWidth: 'sm',
				preventBackdropClose: true,
				...DialogProps,
			}}
			dialogActions={({formId, submitEnabled, isSubmitting}) => (
				<CategoryCollisionDialogActions>
					<CategoryCollisionDialogActionsLeft>
						{onCancel ? (
							<Button
								// className={classes.cancelButton}
								disabled={isSubmitting || DialogProps?.closeDisabled}
								color="primary"
								data-testid="form-cancel"
								onClick={onCancel}
							>
								{CategoryCollisionManagerActionLabel.CANCEL}
							</Button>
						) : null}
					</CategoryCollisionDialogActionsLeft>

					<CategoryCollisionDialogActionsRight>
						<Button
							disabled={!submitEnabled}
							type="submit"
							form={formId}
							variant={'outlined'}
							onClickCapture={(event) => {
								setSkipped(false);
							}}
						>
							{CategoryCollisionManagerActionLabel.SUBMIT}
						</Button>

						<Button
							disabled={!submitEnabled}
							type="submit"
							form={formId}
							variant={'contained'}
							onClickCapture={(event) => {
								setSkipped(true);
							}}
						>
							{CategoryCollisionManagerActionLabel.SKIP}
						</Button>
					</CategoryCollisionDialogActionsRight>
				</CategoryCollisionDialogActions>
			)}
		>
			<CategoryCollisionManager {...props} form={form} />
		</ReactHookFormDialog>
	);
};

export interface CategoryCollisionManagerDialogContainerProps extends Omit<
	CategoryCollisionManagerDialogProps,
	'form' | 'onSubmit'
> {
	onSubmit: (data: ICategoryCollisionFormValues) => Promise<void>;
}

export const CategoryCollisionManagerDialogContainer = ({
	onSubmit: _onSubmit,
	...props
}: CategoryCollisionManagerDialogContainerProps) => {
	const initialValues = useMemo<ICategoryCollisionFormValues>(
		() => ({
			skipped: false,
		}),
		[],
	);

	const {form, onSubmit} = useCategoryCollisionForm({
		initialValues,
		submit: _onSubmit,
	});

	return (
		<CategoryCollisionManagerDialog
			form={form}
			{...props}
			onSubmit={onSubmit}
		/>
	);
};
